import { z } from 'zod';
import { t } from 'i18next';
import { CommunicationCampaignRecipient } from '../enums';

const communicationCampaignType = z.enum(['mail', 'sms', 'whatsapp']);

export const CommunicationCampaignFormValidatorSchema = z.object({
  type: communicationCampaignType,
  name: z.string().min(1, 'Required'),
  isMarketing: z.boolean(),
  subject: z.string().nullish(),
  replyTo: z.string().email().nullish(),
  body: z.string().nullish(),
  recipient: z.enum(CommunicationCampaignRecipient).nullish(),
  wishlist: z.string().nullish(),
  badge: z.string().nullish(),
  selectedFanIds: z.array(z.string()).optional(),
  whatsappTemplateName: z.string().nullish(),
  whatsappTemplateLanguage: z.string().nullish(),
  whatsappTemplateHasHeader: z.boolean().nullish(),
  whatsappTemplateParams: z.array(z.string()).nullish(),
  whatsappHeaderImageKey: z.string().nullish(),
  whatsappTemplateButtonIndex: z.number().nullish(),
  whatsappButtonUrlSuffix: z.string().nullish(),
});
export type CommunicationCampaignFormValues = z.infer<
  typeof CommunicationCampaignFormValidatorSchema
>;

type CampaignChannel = 'mail' | 'sms' | 'whatsapp';

export const whatsappVariableSchema = z
  .string()
  .trim()
  .min(
    1,
    t('mailcampaigns_whatsapp_variables_required')
      ? (t('mailcampaigns_whatsapp_variables_required') as string)
      : 'All variables are required'
  )
  .refine((value) => !/[\n\t]/.test(value), {
    message: t('communication_whatsapp_variable_single_line')
      ? (t('communication_whatsapp_variable_single_line') as string)
      : 'Variables must be a single line',
  });

// Common validation refinements shared between mail, SMS and WhatsApp
const createRequiredFormSchema = (channel: CampaignChannel) => {
  const baseSchema = z.object({
    name: z
      .string()
      .min(
        1,
        t('mailcampaigns_name_required')
          ? (t('mailcampaigns_name_required') as string)
          : 'Required'
      ),
    recipient: z.enum(CommunicationCampaignRecipient, {
      message: t('mailcampaigns_recipient_required')
        ? (t('mailcampaigns_recipient_required') as string)
        : 'Required',
    }),
    wishlist: z.object({ id: z.string().nullish() }).nullish(),
    badge: z.object({ id: z.string().nullish() }).nullish(),
    selectedFanIds: z.array(z.string()).optional(),
    ...(channel !== 'whatsapp' && {
      body: z.string({
        message: t('mailcampaigns_body_required')
          ? (t('mailcampaigns_body_required') as string)
          : 'Required',
      }),
    }),
    ...(channel === 'mail' && {
      subject: z.string({
        message: t('mailcampaigns_subject_required')
          ? (t('mailcampaigns_subject_required') as string)
          : 'Required',
      }),
      replyTo: z
        .string({
          message: t('mailcampaigns_reply_to_required')
            ? (t('mailcampaigns_reply_to_required') as string)
            : 'Required',
        })
        .email({
          message: t('mailcampaigns_reply_to_invalid')
            ? (t('mailcampaigns_reply_to_invalid') as string)
            : 'Required',
        }),
    }),
    ...(channel === 'whatsapp' && {
      whatsappTemplateName: z.string({
        message: t('mailcampaigns_whatsapp_template_required')
          ? (t('mailcampaigns_whatsapp_template_required') as string)
          : 'Required',
      }),
      whatsappTemplateLanguage: z.string({
        message: t('mailcampaigns_whatsapp_language_required')
          ? (t('mailcampaigns_whatsapp_language_required') as string)
          : 'Required',
      }),
      whatsappTemplateParams: z.array(whatsappVariableSchema),
    }),
  });

  return (
    baseSchema
      // Validate selectedFanIds for PARTIAL_FANGROUP and WAITING_LIST_DISTRIBUTED
      .refine(
        (data) => {
          if (
            data.recipient ===
              CommunicationCampaignRecipient.PARTIAL_FANGROUP ||
            data.recipient ===
              CommunicationCampaignRecipient.WAITING_LIST_DISTRIBUTED
          ) {
            return data.selectedFanIds && data.selectedFanIds.length > 0;
          }
          return true;
        },
        {
          message: t('mailcampaigns_selected_fans_required')
            ? (t('mailcampaigns_selected_fans_required') as string)
            : 'Select at least one fan, or choose a different recipient type',
          path: ['recipient'],
        }
      )
      // Validate badge for BADGE recipient
      .refine(
        (data) => {
          if (data.recipient === CommunicationCampaignRecipient.BADGE) {
            return !!data.badge?.id;
          }
          return true;
        },
        {
          message: t('mailcampaigns_badge_required')
            ? (t('mailcampaigns_badge_required') as string)
            : 'Please select a badge',
          path: ['badge'],
        }
      )
      // Validate wishlist for wishlist-based recipients
      .refine(
        (data) => {
          if (
            data.recipient === CommunicationCampaignRecipient.WAITING_LIST ||
            data.recipient ===
              CommunicationCampaignRecipient.WAITING_LIST_NOT_DISTRIBUTED ||
            data.recipient ===
              CommunicationCampaignRecipient.WAITING_LIST_DISTRIBUTED ||
            data.recipient ===
              CommunicationCampaignRecipient.WAITING_LIST_DISTRIBUTED_ACCEPTED ||
            data.recipient ===
              CommunicationCampaignRecipient.WAITING_LIST_DISTRIBUTED_NOT_ACCEPTED
          ) {
            return !!data.wishlist?.id;
          }
          return true;
        },
        {
          message: t('mailcampaigns_wishlist_required')
            ? (t('mailcampaigns_wishlist_required') as string)
            : 'Please select an experience',
          path: ['wishlist'],
        }
      )
  );
};

export const MailcampaignRequiredFormSchema = createRequiredFormSchema('mail');
export const SMSCampaignRequiredFormSchema = createRequiredFormSchema('sms');
export const WhatsappCampaignRequiredFormSchema =
  createRequiredFormSchema('whatsapp');

export const createCommunicationCampaignBodySchema = z.object({
  name: z.string(),
  fangroup: z.string(),
  isMarketing: z.boolean(),
  subject: z.string().nullish(),
  replyTo: z.string().nullish(),
  body: z.string().nullish(),
  recipient: z.enum(CommunicationCampaignRecipient).nullish(),
  wishlist: z.string().nullish(),
  badge: z.string().nullish(),
  selectedFanIds: z.array(z.string()).optional(),
  type: communicationCampaignType,
  whatsappTemplateName: z.string().nullish(),
  whatsappTemplateLanguage: z.string().nullish(),
  whatsappTemplateHasHeader: z.boolean().nullish(),
  whatsappTemplateParams: z.array(z.string()).nullish(),
  whatsappHeaderImageKey: z.string().nullish(),
  whatsappTemplateButtonIndex: z.number().nullish(),
  whatsappButtonUrlSuffix: z.string().nullish(),
});

// Type inference if needed
export type CreateCommunicationCampaignBody = z.infer<
  typeof createCommunicationCampaignBodySchema
>;

/**
 * PATCH body: every field is optional so a caller sends only what it changes
 * (see `.claude/rules/data-integrity.md`). `fangroup` and `type` are fixed at
 * creation time and cannot be patched.
 */
export interface UpdateCommunicationCampaignBody
  extends Partial<
    Omit<CreateCommunicationCampaignBody, 'fangroup' | 'name' | 'type'>
  > {
  name?: string;
  mailAttachmentKey?: string | null;
}
